/**
 * Input de fórmula con sugerencias de columnas.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const FN_SUGGESTIONS = [
    { name: 'SUM', kind: 'fn', insert: 'SUM(' },
    { name: 'MAX', kind: 'fn', insert: 'MAX(' },
    { name: 'MIN', kind: 'fn', insert: 'MIN(' },
    { name: 'PROMEDIO', kind: 'fn', insert: 'PROMEDIO(' },
    { name: 'COUNT', kind: 'fn', insert: 'COUNT(' },
    { name: 'SUMA', kind: 'fn', insert: 'SUMA(' }
];

const KIND_LABEL = {
    dimension: 'dim',
    metric: 'métrica',
    calc: 'calc',
    fn: 'función'
};

function tokenAt(text, cursor) {
    const before = String(text || '').slice(0, cursor);
    const match = before.match(/([A-Za-zÁÉÍÓÚÜÑáéíóúüñ_%][\wÁÉÍÓÚÜÑáéíóúüñ.%]*)$/);
    if (!match) return { start: cursor, query: '' };
    return { start: cursor - match[1].length, query: match[1] };
}

const FormulaInput = ({
    value = '',
    onChange,
    suggestions = [],
    placeholder,
    multiline = true,
    style,
    includeFunctions = true
}) => {
    const ref = useRef(null);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [active, setActive] = useState(0);
    const [menuPos, setMenuPos] = useState(null);

    const catalog = useMemo(() => {
        const fields = (suggestions || []).map(item => (
            typeof item === 'string'
                ? { name: item, kind: 'metric', insert: item }
                : { name: item.name, kind: item.kind || 'metric', insert: item.insert || item.name }
        ));
        return includeFunctions ? [...FN_SUGGESTIONS, ...fields] : fields;
    }, [suggestions, includeFunctions]);

    const { start, query } = tokenAt(value, cursor);
    const q = query.toLowerCase();
    const matches = useMemo(() => {
        if (!open) return [];
        const ranked = catalog
            .filter(item => !q || item.name.toLowerCase().includes(q))
            .sort((a, b) => {
                const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
                const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
                if (aStarts !== bStarts) return aStarts - bStarts;
                if (a.kind === 'fn' && b.kind !== 'fn' && q.length < 2) return -1;
                if (b.kind === 'fn' && a.kind !== 'fn' && q.length < 2) return 1;
                return a.name.localeCompare(b.name, 'es');
            })
            .slice(0, 12);
        return ranked;
    }, [catalog, q, open]);

    useEffect(() => { setActive(0); }, [query, open]);

    useEffect(() => {
        if (!open || !ref.current) {
            setMenuPos(null);
            return undefined;
        }
        const place = () => {
            const rect = ref.current.getBoundingClientRect();
            const menuH = 180;
            const top = (window.innerHeight - rect.bottom < menuH && rect.top > menuH)
                ? rect.top - menuH - 2
                : rect.bottom + 2;
            setMenuPos({
                top: Math.max(8, top),
                left: rect.left,
                width: Math.max(rect.width, 180)
            });
        };
        place();
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open, value, matches.length]);

    useEffect(() => {
        const onDoc = (event) => {
            const t = event.target;
            if (wrapRef.current?.contains(t) || menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const applyItem = (item) => {
        const el = ref.current;
        const pos = el ? el.selectionStart : cursor;
        const { start: from } = tokenAt(value, pos);
        const next = `${value.slice(0, from)}${item.insert}${value.slice(pos)}`;
        const caret = from + item.insert.length;
        onChange(next);
        setOpen(false);
        requestAnimationFrame(() => {
            if (!ref.current) return;
            ref.current.focus();
            ref.current.setSelectionRange(caret, caret);
            setCursor(caret);
        });
    };

    const onKeyDown = (event) => {
        if (open && matches.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive(i => (i + 1) % matches.length);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive(i => (i - 1 + matches.length) % matches.length);
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                applyItem(matches[active] || matches[0]);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                return;
            }
        }
    };

    const syncCursor = (event) => {
        setCursor(event.target.selectionStart || 0);
    };

    const common = {
        ref,
        value,
        placeholder,
        spellCheck: false,
        onChange: (e) => {
            onChange(e.target.value);
            setCursor(e.target.selectionStart || 0);
            setOpen(true);
        },
        onKeyDown,
        onClick: syncCursor,
        onKeyUp: syncCursor,
        onFocus: (e) => {
            setCursor(e.target.selectionStart || 0);
            setOpen(true);
        },
        style: { ...style, width: '100%', boxSizing: 'border-box' }
    };

    return (
        <div ref={wrapRef} style={styles.wrap}>
            {multiline
                ? <textarea {...common} rows={3} />
                : <input type="text" {...common} />}
            {open && matches.length > 0 && (q || value.endsWith('(')) && menuPos && createPortal(
                <div ref={menuRef} style={{ ...styles.menu, top: menuPos.top, left: menuPos.left, width: menuPos.width }} role="listbox">
                    {matches.map((item, index) => (
                        <button
                            key={`${item.kind}-${item.name}`}
                            type="button"
                            role="option"
                            aria-selected={index === active}
                            style={{
                                ...styles.option,
                                background: index === active ? '#f3e5f5' : '#fff'
                            }}
                            onMouseDown={(e) => { e.preventDefault(); applyItem(item); }}
                            onMouseEnter={() => setActive(index)}
                        >
                            <span style={styles.optionName}>{item.name}</span>
                            <span style={styles.optionKind}>{KIND_LABEL[item.kind] || item.kind}</span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

const styles = {
    wrap: { position: 'relative', width: '100%' },
    menu: {
        position: 'fixed',
        zIndex: 4000,
        background: '#fff',
        border: '1px solid #d1c4e9',
        borderRadius: '8px',
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        maxHeight: '180px',
        overflowY: 'auto'
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        border: 'none',
        textAlign: 'left',
        padding: '6px 8px',
        cursor: 'pointer',
        fontSize: '0.75rem'
    },
    optionName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: '#202124' },
    optionKind: { fontSize: '0.62rem', color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: '0.03em', flexShrink: 0 }
};

export default FormulaInput;
